import React from 'react';
import { Box, Card, CardContent, Divider, Skeleton, Tooltip, Typography } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { formatDateTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import { DashboardMetricItem } from '../../api/dashboard';

interface DashboardMetricSectionProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  metricData?: DashboardMetricItem;
  isLoading: boolean;
  renderContent: (data: DashboardMetricItem) => React.ReactNode;
  themeColor?: string; // カスタムテーマカラー
  renderTitleCount?: () => React.ReactNode; // タイトルの横に表示するカウント表示用関数
  renderButton?: React.ReactNode; // 右端に表示するボタン
}

/**
 * ダッシュボードの各メトリクスセクションの共通レイアウトコンポーネント
 */
const DashboardMetricSection: React.FC<DashboardMetricSectionProps> = ({
  title,
  description,
  icon,
  metricData,
  isLoading,
  renderContent,
  themeColor,
  renderTitleCount,
  renderButton
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        mb: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0'
      }}
    >
      <CardContent>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          justifyContent: 'space-between' // 左右に分ける
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                color: themeColor || 'text.primary',
                fontSize: '1rem',
                borderLeft: `3px solid ${themeColor || '#757575'}`,
                pl: 1.5,
                py: 0.3,
                fontWeight: 'medium',
                opacity: 0.95
              }}
            >
              {title}
            </Typography>
            {/* スロットを追加して追加の情報を表示できるようにする */}
            {renderTitleCount && renderTitleCount()}
          </Box>

          {/* 更新ボタンを右端に表示 */}
          {renderButton}
        </Box>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        <Divider sx={{ my: 2 }}/>

        {isLoading ? (
          <Box>
            <Skeleton variant="rectangular" height={100}/>
            <Skeleton variant="text" sx={{ mt: 1 }}/>
            <Skeleton variant="text" width="60%"/>
          </Box>
        ) : metricData ? (
          <>
            {renderContent(metricData)}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Tooltip
                title={`最終更新日時: ${formatDateTime(metricData.executed_at)}`}
                arrow
                placement="left"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    最終更新: {getHumanReadableTimeAgo(metricData.executed_at)}
                  </Typography>
                  <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.8rem', opacity: 0.7 }}/>
                </Box>
              </Tooltip>
            </Box>
          </>
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            データがありません
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
export default DashboardMetricSection;
